(() => {
  if (window.UncommonConfigurableHomeLoaded) return;
  window.UncommonConfigurableHomeLoaded = true;

  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pad = (value) => String(value).padStart(2, '0');

  const initProductSlider = (root) => {
    if (root.dataset.ucSliderReady === 'true') return;
    const track = root.querySelector('[data-uc-product-track]');
    const slides = Array.from(root.querySelectorAll('.uncommon-product-card'));
    const previous = root.querySelector('[data-uc-product-prev]');
    const next = root.querySelector('[data-uc-product-next]');
    const status = root.querySelector('[data-uc-product-status]');
    if (!track || slides.length === 0) return;
    root.dataset.ucSliderReady = 'true';
    let frame = 0;

    const positions = () => slides.map((slide) => Math.max(0, slide.offsetLeft - track.offsetLeft));
    const currentIndex = () => {
      const points = positions();
      return points.reduce((best, point, index) => (
        Math.abs(point - track.scrollLeft) < Math.abs(points[best] - track.scrollLeft) ? index : best
      ), 0);
    };
    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const update = () => {
      frame = 0;
      const index = currentIndex();
      if (status) status.textContent = `${pad(index + 1)} / ${pad(slides.length)}`;
      root.dataset.ucSliderOverflow = String(maxScroll() > 1);
      if (previous) previous.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= maxScroll() - 1;
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    const move = (direction) => {
      const index = Math.min(slides.length - 1, Math.max(0, currentIndex() + direction));
      track.scrollTo({ left: Math.min(positions()[index], maxScroll()), behavior: reduceMotionQuery.matches ? 'auto' : 'smooth' });
    };

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    track.addEventListener('scroll', requestUpdate, { passive: true });
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
    });
    if ('ResizeObserver' in window) new ResizeObserver(requestUpdate).observe(track);
    else window.addEventListener('resize', requestUpdate, { passive: true });
    update();
  };

  const initCollectionTabs = (root) => {
    if (root.dataset.ucTabsReady === 'true') return;
    const tabs = Array.from(root.querySelectorAll('[data-uc-collection-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-uc-collection-panel]'));
    if (panels.length === 0) return;
    root.dataset.ucTabsReady = 'true';
    panels.forEach((panel) => panel.querySelectorAll('[data-uc-product-slider]').forEach(initProductSlider));

    const activate = (tab, focus = false) => {
      const panelId = tab.getAttribute('aria-controls');
      tabs.forEach((candidate) => {
        const active = candidate === tab;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-selected', String(active));
        candidate.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = panel.id === panelId;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        if (active) {
          const track = panel.querySelector('[data-uc-product-track]');
          if (track) track.scrollLeft = 0;
          panel.querySelectorAll('[data-uc-product-slider]').forEach((slider) => {
            slider.dataset.ucSliderReady = 'false';
            initProductSlider(slider);
          });
        }
      });
      if (focus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        let target = null;
        if (event.key === 'ArrowLeft') target = tabs[(index - 1 + tabs.length) % tabs.length];
        if (event.key === 'ArrowRight') target = tabs[(index + 1) % tabs.length];
        if (event.key === 'Home') target = tabs[0];
        if (event.key === 'End') target = tabs[tabs.length - 1];
        if (target) { event.preventDefault(); activate(target, true); }
      });
    });
  };

  const initTestimonials = (root) => {
    if (root.dataset.ucTestimonialsReady === 'true') return;
    const track = root.querySelector('[data-uc-testimonial-track]');
    const slides = Array.from(root.querySelectorAll('[data-uc-testimonial-slide]'));
    if (!track || slides.length === 0) return;
    root.dataset.ucTestimonialsReady = 'true';

    const previous = root.querySelector('[data-uc-testimonial-prev]');
    const next = root.querySelector('[data-uc-testimonial-next]');
    const dots = Array.from(root.querySelectorAll('[data-uc-testimonial-dot]'));
    const status = root.querySelector('[data-uc-testimonial-status]');
    const progress = root.querySelector('[data-uc-testimonial-progress]');
    const pauseToggle = root.querySelector('[data-uc-testimonial-pause]');
    const autoplay = root.dataset.autoplay === 'true';
    const intervalSeconds = Math.min(10, Math.max(3, Number(root.dataset.interval) || 6));
    const pauses = new Set();
    let index = 0;
    let timer = 0;
    let pointerStartX = null;

    const stopTimer = () => {
      if (timer) window.clearTimeout(timer);
      timer = 0;
    };
    const canAutoplay = () => autoplay && slides.length > 1 && !reduceMotionQuery.matches && pauses.size === 0;
    const schedule = () => {
      stopTimer();
      if (canAutoplay()) timer = window.setTimeout(() => goTo(index + 1, false), intervalSeconds * 1000);
    };
    const goTo = (nextIndex, manual = true) => {
      index = (nextIndex + slides.length) % slides.length;
      track.style.transform = `translate3d(${-index * 100}%, 0, 0)`;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if ('inert' in slide) slide.inert = !active;
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
      if (status) status.textContent = `${pad(index + 1)} / ${pad(slides.length)}`;
      if (progress) progress.style.transform = `scaleX(${(index + 1) / slides.length})`;
      if (manual) stopTimer();
      schedule();
    };
    const pause = (reason) => { pauses.add(reason); stopTimer(); };
    const resume = (reason) => { pauses.delete(reason); schedule(); };
    const updatePauseToggle = () => {
      if (!pauseToggle) return;
      const userPaused = pauses.has('user');
      pauseToggle.setAttribute('aria-pressed', String(userPaused));
      pauseToggle.textContent = userPaused ? pauseToggle.dataset.resumeLabel : pauseToggle.dataset.pauseLabel;
    };

    previous?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));
    dots.forEach((dot) => dot.addEventListener('click', () => goTo(Number(dot.dataset.index))));
    pauseToggle?.addEventListener('click', () => {
      if (pauses.has('user')) resume('user');
      else pause('user');
      updatePauseToggle();
    });
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); goTo(index + 1); }
    });
    root.addEventListener('mouseenter', () => pause('hover'));
    root.addEventListener('mouseleave', () => resume('hover'));
    root.addEventListener('focusin', () => pause('focus'));
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) resume('focus');
    });
    root.addEventListener('pointerdown', (event) => {
      pointerStartX = event.clientX;
      pause('pointer');
    });
    root.addEventListener('pointerup', (event) => {
      if (pointerStartX !== null) {
        const delta = event.clientX - pointerStartX;
        if (Math.abs(delta) >= 40) goTo(index + (delta < 0 ? 1 : -1));
      }
      pointerStartX = null;
      resume('pointer');
    });
    root.addEventListener('pointercancel', () => { pointerStartX = null; resume('pointer'); });

    const visibilityHandler = () => document.hidden ? pause('hidden') : resume('hidden');
    document.addEventListener('visibilitychange', visibilityHandler);
    const motionHandler = () => schedule();
    reduceMotionQuery.addEventListener?.('change', motionHandler);

    root.ucCleanup = () => {
      stopTimer();
      document.removeEventListener('visibilitychange', visibilityHandler);
      reduceMotionQuery.removeEventListener?.('change', motionHandler);
    };
    updatePauseToggle();
    goTo(0, false);
  };

  const init = (scope = document) => {
    if (scope.matches?.('[data-uc-product-slider]')) initProductSlider(scope);
    if (scope.matches?.('[data-uncommon-collection-tabs]')) initCollectionTabs(scope);
    if (scope.matches?.('[data-uncommon-testimonials]')) initTestimonials(scope);
    scope.querySelectorAll?.('[data-uc-product-slider]').forEach(initProductSlider);
    scope.querySelectorAll?.('[data-uncommon-collection-tabs]').forEach(initCollectionTabs);
    scope.querySelectorAll?.('[data-uncommon-testimonials]').forEach(initTestimonials);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) init(node);
    }));
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelector?.('[data-uncommon-testimonials]')?.ucCleanup?.());
})();
